import React from 'react';
import {
  Users,
  QrCode,
  Play,
  Timer,
  Zap,
  Smartphone,
  EyeOff
} from 'lucide-react';
import { Header } from './Header.jsx';
import { Footer } from './Footer.jsx';
import { SeoContent } from './SeoContent.jsx';

const AVATARS = ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮'];

// Static components that don't require state
const Button = ({ children, variant = 'primary', className = '', disabled = false, icon: Icon, id, onClick }) => {
  const baseStyle = "w-full py-3 px-4 rounded-xl font-bold shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-indigo-500/30",
    secondary: "bg-gray-700 text-white hover:bg-gray-600",
    success: "bg-green-500 text-white shadow-green-500/30",
  };

  return (
    <button
      id={id}
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyle} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : ''} ${className}`}
    >
      {Icon && <Icon size={20} />}
      {children}
    </button>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-gray-800/80 backdrop-blur-md border border-gray-700 rounded-2xl p-6 shadow-xl ${className}`}>
    {children}
  </div>
);

// Home page is controlled via props to stay SSR-friendly and hydrate cleanly
export const HomePage = ({
  playerName = '',
  playerAvatar = AVATARS[0],
  roomCode = '',
  onAvatarChange = () => {},
  onNameChange = () => {},
  onRoomCodeChange = () => {},
  onCreate = () => {},
  onJoin = () => {},
  onLocal = () => {},
  disableCreate = false,
  disableJoin = false
}) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      {/* Three-column layout for Auto Ads */}
      <div className="flex-grow flex flex-row w-full max-w-7xl mx-auto px-0 sm:px-2 lg:px-4">

        {/* Left Sidebar - Auto Ads insertion area */}
        <aside className="hidden lg:block w-48 xl:w-64 flex-shrink-0 pt-8">
          {/* Empty space for Auto Ads to insert ads */}
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-2xl mx-auto pt-4 lg:pt-8 pb-0 px-2 sm:px-4 lg:px-6 space-y-6 lg:space-y-8">
          {/* Top spacer - Auto Ads can insert banner here */}
          <div className="hidden lg:block lg:min-h-[120px]"></div>

          {/* Hero Title Section */}
          <div className="text-center space-y-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black animate-fade-in leading-tight flex items-center justify-center gap-2 sm:gap-3 lg:gap-4">
            <div className="inline-block animate-float flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-500 blur-3xl opacity-50 animate-pulse-glow"></div>
                <EyeOff className="text-indigo-400 relative z-10 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16" />
              </div>
            </div>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 animate-gradient whitespace-nowrap">
              Imposter Game
            </span>
          </h1>

          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 animate-fade-in-delay-1 px-2">
            Who's the Spy? Test Your Acting & Observation Skills
          </p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center text-xs md:text-sm text-gray-300 animate-fade-in-delay-2 pt-2">
            <span className="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-full backdrop-blur-sm">
              <Users size={16} /> <span className="font-semibold">3-12 Players</span>
            </span>
            <span className="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-full backdrop-blur-sm">
              <Timer size={16} /> <span className="font-semibold">5-10 Minutes</span>
            </span>
            <span className="flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-full backdrop-blur-sm">
              <Zap size={16} /> <span className="font-semibold">Quick & Fun</span>
            </span>
          </div>
        </div>

        {/* Mid spacer - Auto Ads insertion point */}
        <div className="hidden lg:block lg:min-h-[100px]"></div>

        {/* Form Section - Static version */}
        <div className="w-full max-w-md mx-auto">
          <Card className="space-y-6">
            {/* 1. Identity Section */}
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider ml-1 font-bold">1. Choose Avatar</label>
                <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
                  {AVATARS.map((av, i) => (
                    <button
                      key={av}
                      data-avatar={av}
                      onClick={() => onAvatarChange(av)}
                      className={`avatar-btn text-2xl min-w-[48px] h-[48px] flex items-center justify-center rounded-full transition-all duration-200 ${
                        playerAvatar === av
                          ? 'bg-indigo-600 scale-110 shadow-lg ring-2 ring-indigo-400'
                          : 'bg-gray-700 hover:bg-gray-600 opacity-70 hover:opacity-100'
                      }`}
                    >
                      {av}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider ml-1 font-bold">2. Your Nickname</label>
                <input
                  type="text"
                  id="player-name-input"
                  value={playerName}
                  onChange={(e) => onNameChange(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-xl p-4 mt-1 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-lg font-medium placeholder:text-gray-600"
                  placeholder="Enter your name..."
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-700/50 my-2"></div>

            {/* 2. Action Section: Create */}
            <div className="flex flex-col gap-3">
              <Button
                id="create-room-btn"
                onClick={onCreate}
                icon={Play}
                disabled={disableCreate}
                className="bg-gradient-to-r from-indigo-600 to-blue-600 py-4 text-lg shadow-indigo-500/20"
              >
                Create Online Room
              </Button>

              <Button
                id="local-play-btn"
                onClick={onLocal}
                icon={Smartphone}
                variant="secondary"
                className="py-4 text-lg"
              >
                Pass & Play (Local)
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 border-t border-gray-700"></div>
                <span className="text-xs text-gray-500 uppercase tracking-wider">Or</span>
                <div className="flex-1 border-t border-gray-700"></div>
              </div>

              {/* 3. Join Room */}
              <div className="space-y-3">
                <label className="text-xs text-gray-400 uppercase tracking-wider ml-1 font-bold">Join a Friend's Room</label>
                <input
                  type="text"
                  id="room-code-input"
                  value={roomCode}
                  onChange={(e) => onRoomCodeChange(e.target.value.toUpperCase())}
                  className="w-full bg-gray-900 border border-gray-700 text-white rounded-xl p-3 focus:ring-2 focus:ring-green-500 outline-none font-mono text-center tracking-widest placeholder:text-gray-600"
                  placeholder="ENTER CODE"
                  maxLength={6}
                />
                <Button
                  id="join-room-btn"
                  onClick={onJoin}
                  icon={QrCode}
                  variant="success"
                  disabled={disableJoin || !roomCode.trim()}
                  className="py-4 text-lg"
                >
                  Join Game
                </Button>
              </div>
            </div>
          </Card>
        </div>

          {/* Top spacer for Auto Ads */}
          <div className="hidden lg:block lg:min-h-[100px]"></div>

          {/* SEO Content at bottom */}
          <SeoContent />

          {/* Bottom spacer for Auto Ads */}
          <div className="hidden lg:block lg:min-h-[100px]"></div>
        </main>

        {/* Right Sidebar - Auto Ads insertion area */}
        <aside className="hidden lg:block w-48 xl:w-64 flex-shrink-0 pt-8">
          {/* Empty space for Auto Ads to insert ads */}
        </aside>

      </div>

      <Footer />
    </div>
  );
};
